import { NotAuthorizedIntent } from "@/@types/NotAuthorizedIntent";
import { declineReasonForDisplay } from "@/utils/declineReasonForDisplay";
import BadgeShared from "@/components/shared/Badge/Badge";
import { Card } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import { AlertCircle, ChevronDown, Clock, Hash, XCircle } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import ErrorHistoryCircle from "@/assets/icons/ErrorHistoryCircle";

interface NotAuthorizedIntentsListProps {
  intents: NotAuthorizedIntent[];
  className?: string;
  collapsible?: boolean;
}

export function NotAuthorizedIntentsContent({
  intents,
}: {
  intents: NotAuthorizedIntent[];
}) {
  const { t } = useTranslation(["sales", "common"]);

  if (!intents || intents.length === 0) {
    return null;
  }

  return (
    <>
      <motion.div
        className="space-y-2.5"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {intents.map((intent, index) => {
          const isLast = index === intents.length - 1;

          return (
            <motion.div key={intent.id} variants={itemVariants}>
              <div
                className={cn(
                  "relative p-3.5 rounded-xl border border-zinc-800/80",
                  "bg-gradient-to-br from-zinc-900/50 to-zinc-950/50",
                  "group transition-all duration-200",
                  "hover:border-zinc-700/80 hover:shadow-lg hover:shadow-black/20",
                )}
              >
                <div
                  className={cn(
                    "absolute left-0 top-3 bottom-3 w-0.5 rounded-full transition-all",
                    getStatusColor(isLast),
                  )}
                />

                <div className="flex items-start justify-between gap-3 pl-2">
                  <div className="flex flex-col gap-2 min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <XCircle size={14} className="text-[#F87171]" />
                      <span className="text-sm font-medium text-colorText truncate group-hover:text-white transition-colors">
                        {declineReasonForDisplay(
                          { error_code: intent.error_code },
                          t,
                        )}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      <BadgeShared status="failed" />

                      {isLast && (
                        <span className="text-[10px] font-medium text-[#F87171] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#F87171]/10 border border-[#F87171]/20">
                          {t("last_attempt")}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground/70">
                      <span className="flex items-center gap-1">
                        <Clock size={10} />
                        {intent.created_at_formatted}
                      </span>

                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-zinc-800/60 hover:bg-zinc-800 transition-colors cursor-default">
                              <Hash
                                size={10}
                                className="text-[#F87171] shrink-0"
                              />
                              <code className="text-[#F87171]/80">
                                {intent.raw_acquirer_code}
                              </code>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{t("raw_acquirer_code_description")}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </motion.div>

      <div className="mt-4 pt-4 border-t border-zinc-800/50">
        <div className="flex items-center flex-wrap gap-2 sm:gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-[#F87171]" />
            {intents.length === 1
              ? t("single_payment_attempt")
              : t("total_payment_attempts", { count: intents.length })}
          </span>
        </div>
      </div>
    </>
  );
}

const getStatusColor = (isLast: boolean) => {
  return isLast ? "bg-[#F87171]" : "bg-zinc-600";
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: "easeOut",
    },
  },
};

const contentVariants = {
  collapsed: {
    height: 0,
    opacity: 0,
    transition: {
      height: { duration: 0.3, ease: "easeInOut" },
      opacity: { duration: 0.2 },
    },
  },
  expanded: {
    height: "auto",
    opacity: 1,
    transition: {
      height: { duration: 0.3, ease: "easeInOut" },
      opacity: { duration: 0.2, delay: 0.1 },
    },
  },
};

export function NotAuthorizedIntentsList({
  intents,
  className,
  collapsible = false,
}: NotAuthorizedIntentsListProps) {
  const { t } = useTranslation(["sales", "common"]);
  const [isExpanded, setIsExpanded] = useState(false);

  if (!intents || intents.length === 0) {
    return null;
  }

  return (
    <Card className={cn("p-5 md:p-6", className)}>
      <div
        className={cn(
          "flex items-center justify-between",
          collapsible ? "cursor-pointer select-none" : "mb-5",
          collapsible && !isExpanded && "mb-0",
          collapsible && isExpanded && "mb-5",
        )}
        onClick={collapsible ? () => setIsExpanded(!isExpanded) : undefined}
      >
        <div className="flex items-center gap-2">
          <ErrorHistoryCircle />
          <h2 className="text-colorText font-semibold text-base">
            {t("refusal_history")}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <AlertCircle size={12} className="text-[#F87171]" />
            <span>{intents.length}</span>
          </div>
          {collapsible && (
            <ChevronDown
              size={16}
              className={cn(
                "text-muted-foreground transition-transform duration-300",
                isExpanded && "rotate-180",
              )}
            />
          )}
        </div>
      </div>

      <AnimatePresence initial={false}>
        {(!collapsible || isExpanded) && (
          <motion.div
            initial={collapsible ? "collapsed" : false}
            animate="expanded"
            exit="collapsed"
            variants={contentVariants}
            className="overflow-hidden"
          >
            <NotAuthorizedIntentsContent intents={intents} />
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}
